import type { ReactNode } from "react";
import clsx from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SearchInput } from "./Field";
import { Spinner, EmptyState } from "./Misc";
import { Skeleton } from "./Skeleton";
import { Checkbox } from "./Field";
import { StatusBadge, type Audience } from "../status/StatusBadge";
import type { StatusDomain } from "../status/statusMap";
import { useT } from "../i18n";
import { fmtNumber } from "../format";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  className?: string;
  width?: string;
  /** Status column: renders `row[key]` as a compact StatusBadge of this domain. Put it right after the name (§2). */
  status?: { domain: StatusDomain; audience?: Audience };
  /** Right-align numbers. */
  align?: "left" | "right";
}

export interface DataTableProps<T> {
  /** No outer border/radius – for a table inside a Card or panel. */
  flush?: boolean;
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: string;
  /** The whole row is the link; rows become keyboard-focusable. */
  onRowClick?: (row: T) => void;
  /** Highlights the row (`aria-selected`), e.g. the one open in a side panel. */
  selectedKey?: string | null;
  empty?: ReactNode;
  /** Server-side pagination. */
  page?: number; pageSize?: number; total?: number; onPageChange?: (page: number) => void;
  /** Search box bound to a query string. */
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  toolbar?: ReactNode;
  /** @deprecated use density="admin" with compact badges; kept for the product apps. */
  dense?: boolean;
  /** Row height: admin 44 px, portal 56 px (§1.2). Defaults to the shell's density. */
  density?: "admin" | "portal";
  /** Checkboxes only where bulk actions exist (§4.1). */
  selection?: { selected: Set<string>; onChange: (next: Set<string>) => void; label?: (row: T) => string };
  /** Footer text in words: "Showing 7 of 9 · sorted by urgency, then name". Replaces the pager text when given. */
  footer?: ReactNode;
  className?: string;
  caption?: string;
}

/** Table on tokens: label headers, 44/56 px rows, status column support, optional selection and pager. */
export function DataTable<T>({ columns, rows, rowKey, loading, error, onRowClick, selectedKey, empty, page, pageSize, total, onPageChange, search, toolbar, dense, density, selection, footer, className, caption, flush }: DataTableProps<T>) {
  const t = useT();
  const pages = total !== undefined && pageSize ? Math.max(1, Math.ceil(total / pageSize)) : undefined;
  const allKeys = rows?.map(rowKey) ?? [];
  const allSelected = selection && allKeys.length > 0 && allKeys.every((k) => selection.selected.has(k));
  const toggleAll = () => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (allSelected) allKeys.forEach((k) => next.delete(k)); else allKeys.forEach((k) => next.add(k));
    selection.onChange(next);
  };
  const toggle = (k: string) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    selection.onChange(next);
  };
  const cols = columns.length + (selection ? 1 : 0);
  return (
    <div className={clsx(flush ? "overflow-hidden" : "iga-card overflow-hidden", density === "portal" && "iga-portal", density === "admin" && "[--row-h:2.75rem]", className)}>
      {(search || toolbar) && (
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          {search && <SearchInput className="min-w-56 flex-1" value={search.value} placeholder={search.placeholder ?? t("ui.search")} aria-label={search.placeholder ?? t("ui.search")} onChange={(e) => search.onChange(e.target.value)} />}
          {toolbar}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className={clsx("iga-table", dense && "[&_td]:py-1")}>
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {selection && <th style={{ width: 40 }}><Checkbox aria-label="Select all" checked={!!allSelected} onChange={toggleAll} /></th>}
              {columns.map((c) => <th key={c.key} style={{ width: c.width }} className={clsx(c.className, c.align === "right" && "text-right")}>{c.header}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading && (!rows || rows.length === 0) && <tr><td colSpan={cols} className="py-4"><Skeleton variant="table" rows={4} /></td></tr>}
            {loading && rows && rows.length > 0 && <tr><td colSpan={cols} className="py-2 text-center"><Spinner /></td></tr>}
            {error && <tr><td colSpan={cols} className="py-6 text-center font-semibold text-bad" role="alert">{error}</td></tr>}
            {!loading && !error && rows && rows.length === 0 && <tr><td colSpan={cols} className="h-auto p-0">{empty ?? <EmptyState title={t("ui.nothingHereYet")} />}</td></tr>}
            {rows?.map((r) => {
              const k = rowKey(r);
              const selected = selectedKey === k || selection?.selected.has(k);
              return (
                <tr key={k} aria-selected={selected || undefined} tabIndex={onRowClick ? 0 : undefined} role={onRowClick ? "link" : undefined}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  onKeyDown={onRowClick ? (e) => { if (e.key === "Enter" && e.target === e.currentTarget) onRowClick(r); } : undefined}
                  className={clsx(onRowClick && "cursor-pointer focus-ring")}>
                  {selection && <td onClick={(e) => e.stopPropagation()}><Checkbox aria-label={selection.label?.(r) ?? k} checked={selection.selected.has(k)} onChange={() => toggle(k)} /></td>}
                  {columns.map((c) => (
                    <td key={c.key} className={clsx(c.className, c.align === "right" && "text-right tabular-nums")}>
                      {c.status ? <StatusBadge domain={c.status.domain} audience={c.status.audience} status={String((r as Record<string, unknown>)[c.key] ?? "")} compact />
                        : c.render ? c.render(r) : String((r as Record<string, unknown>)[c.key] ?? "")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(footer || (pages !== undefined && page !== undefined && onPageChange)) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3 py-2 text-small text-fg-3">
          <span>{footer ?? (total !== undefined ? t("ui.total", { count: fmtNumber(total) }) : null)}</span>
          {pages !== undefined && page !== undefined && onPageChange && (
            <div className="flex items-center gap-1">
              <button type="button" className="iga-btn-ghost control-sm px-2" disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label={t("ui.previousPage")}><ChevronLeft className="h-4 w-4" aria-hidden="true" /></button>
              <span>{t("ui.page", { page, pages })}</span>
              <button type="button" className="iga-btn-ghost control-sm px-2" disabled={page >= pages} onClick={() => onPageChange(page + 1)} aria-label={t("ui.nextPage")}><ChevronRight className="h-4 w-4" aria-hidden="true" /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
